import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
    ChefHat, 
    Calendar, 
    ShoppingCart, 
    Tag, 
    Sparkles, 
    CheckCircle, 
    ArrowRight, 
    DollarSign,
    Clock,
    Users,
    Lightbulb,
    BookOpen,
    ChevronDown,
    ChevronUp,
    Play,
    Star
} from 'lucide-react';
import { Button } from '../components/ui/button';

const GuideSection = ({ icon: Icon, title, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    
    return (
        <div className="border-2 border-gray-200 rounded-2xl overflow-hidden mb-4">
            <button 
                onClick={() => setIsOpen(!isOpen)}
                className="w-full p-5 bg-white hover:bg-gray-50 transition-colors flex items-center justify-between"
            >
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-cameleon flex items-center justify-center">
                        <Icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-heading font-bold text-lg text-left">{title}</h3>
                </div>
                {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            {isOpen && (
                <div className="p-5 pt-0 bg-white">
                    <div className="border-t pt-5">
                        {children}
                    </div>
                </div>
            )}
        </div>
    );
};

const StepCard = ({ number, title, description, icon: Icon }) => (
    <div className="flex gap-4 mb-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-white font-bold flex items-center justify-center">
            {number}
        </div>
        <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
                {Icon && <Icon className="w-4 h-4 text-primary" />}
                <h4 className="font-bold">{title}</h4>
            </div>
            <p className="text-muted-foreground text-sm">{description}</p>
        </div>
    </div>
);

const TipBox = ({ children, variant = "info" }) => {
    const colors = {
        info: "bg-blue-50 border-blue-200 text-blue-800",
        success: "bg-green-50 border-green-200 text-green-800",
        warning: "bg-amber-50 border-amber-200 text-amber-800"
    };
    
    return (
        <div className={`p-4 rounded-xl border-2 ${colors[variant]} flex items-start gap-3 my-4`}>
            <Lightbulb className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="text-sm">{children}</div>
        </div>
    );
};

export const GuidePage = () => {
    return (
        <div className="min-h-screen py-12 px-4">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
                        <BookOpen className="w-4 h-4" />
                        Guide d'utilisation
                    </div>
                    <h1 className="font-heading text-4xl md:text-5xl font-black text-foreground mb-4">
                        Comment utiliser<br />
                        <span className="text-gradient-cameleon">Recettes Caméléon</span>
                    </h1>
                    <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                        Apprenez à maximiser vos économies avec notre Pack Hebdomadaire et nos outils intelligents
                    </p>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
                    <div className="bg-white rounded-2xl border-2 border-gray-200 p-4 text-center">
                        <DollarSign className="w-8 h-8 text-green-500 mx-auto mb-2" />
                        <p className="font-heading font-bold text-2xl">30-50%</p>
                        <p className="text-sm text-muted-foreground">d'économies possibles</p>
                    </div>
                    <div className="bg-white rounded-2xl border-2 border-gray-200 p-4 text-center">
                        <Clock className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                        <p className="font-heading font-bold text-2xl">30 min</p>
                        <p className="text-sm text-muted-foreground">de planification/semaine</p>
                    </div>
                    <div className="bg-white rounded-2xl border-2 border-gray-200 p-4 text-center">
                        <Users className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                        <p className="font-heading font-bold text-2xl">4-6</p>
                        <p className="text-sm text-muted-foreground">portions par recette</p>
                    </div>
                    <div className="bg-white rounded-2xl border-2 border-gray-200 p-4 text-center">
                        <Star className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                        <p className="font-heading font-bold text-2xl">5</p>
                        <p className="text-sm text-muted-foreground">soupers par pack</p>
                    </div>
                </div>

                {/* Guide Sections */}
                <GuideSection icon={Calendar} title="Qu'est-ce que le Pack Hebdomadaire?" defaultOpen={true}>
                    <p className="mb-4">
                        Le <strong>Pack Hebdomadaire</strong> est votre planificateur de repas intelligent. Chaque semaine, nous vous proposons :
                    </p>
                    <ul className="space-y-3 mb-4">
                        <li className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span><strong>5 recettes de souper</strong> variées et économiques pour la semaine</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span><strong>Une liste d'épicerie complète</strong> générée automatiquement</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span><strong>Les rabais de la semaine</strong> dans les épiceries (Maxi, IGA, Metro, etc.)</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span><strong>Le calcul des économies</strong> basé sur les rabais en cours</span>
                        </li>
                    </ul>
                    
                    <TipBox variant="success">
                        <strong>Astuce Premium :</strong> Les membres Premium ont accès au pack complet avec toutes les recettes et la liste d'épicerie détaillée. Les utilisateurs gratuits peuvent voir un aperçu.
                    </TipBox>
                    
                    <Link to="/weekly-pack">
                        <Button className="btn-pop bg-primary text-white mt-4">
                            <Play className="w-4 h-4 mr-2" />
                            Voir le Pack de la Semaine
                        </Button>
                    </Link>
                </GuideSection>

                <GuideSection icon={ShoppingCart} title="Comment utiliser la Liste d'Épicerie">
                    <p className="mb-4">
                        La liste d'épicerie est générée automatiquement à partir des recettes du pack. Voici comment en tirer le maximum :
                    </p>
                    
                    <StepCard 
                        number={1}
                        title="Consultez la liste avant vos courses"
                        description="Accédez au Pack Hebdomadaire et cliquez sur l'onglet 'Liste d'épicerie'. Tous les ingrédients nécessaires pour la semaine sont regroupés."
                        icon={ShoppingCart}
                    />
                    
                    <StepCard 
                        number={2}
                        title="Repérez les articles en rabais"
                        description="Les ingrédients qui correspondent à des rabais en cours sont affichés en gras orange avec le pourcentage de réduction et le magasin."
                        icon={Tag}
                    />
                    
                    <StepCard 
                        number={3}
                        title="Ajustez les portions si nécessaire"
                        description="Sur chaque page de recette, vous pouvez ajuster le nombre de portions et les quantités d'ingrédients s'adaptent automatiquement."
                        icon={Users}
                    />
                    
                    <StepCard 
                        number={4}
                        title="Planifiez vos achats par magasin"
                        description="Regroupez vos achats par magasin pour profiter des meilleurs rabais dans chaque épicerie."
                        icon={DollarSign}
                    />
                    
                    <TipBox variant="info">
                        <strong>Conseil :</strong> Faites vos courses le dimanche ou le lundi pour avoir accès à tous les rabais de la semaine qui débutent généralement le jeudi ou vendredi.
                    </TipBox>
                </GuideSection>

                <GuideSection icon={Tag} title="Maximiser vos économies avec les Rabais">
                    <p className="mb-4">
                        Notre système intelligent détecte automatiquement quand un ingrédient de votre liste correspond à un rabais en épicerie :
                    </p>
                    
                    <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-5 border-2 border-orange-200 mb-4">
                        <h4 className="font-bold text-orange-800 mb-3 flex items-center gap-2">
                            <Sparkles className="w-5 h-5" />
                            Comment ça fonctionne
                        </h4>
                        <ul className="space-y-2 text-orange-900">
                            <li className="flex items-start gap-2">
                                <ArrowRight className="w-4 h-4 flex-shrink-0 mt-1" />
                                <span>Nous scannons les circulaires des grandes épiceries québécoises</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <ArrowRight className="w-4 h-4 flex-shrink-0 mt-1" />
                                <span>Notre algorithme intelligent associe les rabais aux ingrédients de vos recettes</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <ArrowRight className="w-4 h-4 flex-shrink-0 mt-1" />
                                <span>Les économies sont calculées automatiquement et affichées dans la liste</span>
                            </li>
                        </ul>
                    </div>
                    
                    <h4 className="font-bold mb-3">Exemple concret :</h4>
                    <div className="bg-white rounded-xl border-2 border-gray-200 p-4 mb-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <span className="font-bold text-orange-600">Pommes de terre</span>
                                <span className="text-gray-500 ml-2">(2 pièces)</span>
                            </div>
                            <span className="text-green-600 font-bold">-1.61$</span>
                        </div>
                        <div className="mt-2 text-sm text-orange-600 flex items-center gap-2">
                            <Tag className="w-4 h-4" />
                            Pommes de terre Russet @ Maxi (-46%)
                        </div>
                    </div>
                    
                    <TipBox variant="warning">
                        <strong>Important :</strong> Les rabais changent chaque semaine ! Consultez régulièrement le pack pour voir les nouvelles économies possibles.
                    </TipBox>
                </GuideSection>

                <GuideSection icon={ChefHat} title="Utiliser les Recettes efficacement">
                    <p className="mb-4">
                        Nos recettes sont conçues pour être économiques, simples et délicieuses. Voici comment en profiter au maximum :
                    </p>
                    
                    <StepCard 
                        number={1}
                        title="Lisez l'Astuce du Chef"
                        description="Chaque recette contient une astuce spéciale pour réussir le plat ou économiser sur les ingrédients."
                        icon={Lightbulb}
                    />
                    
                    <StepCard 
                        number={2}
                        title="Ajustez les portions"
                        description="Utilisez le sélecteur de portions pour adapter les quantités à votre famille. Toutes les mesures s'ajustent automatiquement."
                        icon={Users}
                    />
                    
                    <StepCard 
                        number={3}
                        title="Consultez le coût par portion"
                        description="Chaque recette affiche son coût estimé par portion pour vous aider à planifier votre budget."
                        icon={DollarSign}
                    />
                    
                    <StepCard 
                        number={4}
                        title="Explorez les recettes de base"
                        description="Maîtrisez les recettes de base (béchamel, bouillon, etc.) pour économiser sur les produits préparés."
                        icon={BookOpen}
                    />
                    
                    <div className="grid grid-cols-2 gap-4 mt-6">
                        <Link to="/recipes">
                            <Button variant="outline" className="w-full">
                                <ChefHat className="w-4 h-4 mr-2" />
                                Toutes les recettes
                            </Button>
                        </Link>
                        <Link to="/tips">
                            <Button variant="outline" className="w-full">
                                <Lightbulb className="w-4 h-4 mr-2" />
                                Astuces cuisine
                            </Button>
                        </Link>
                    </div>
                </GuideSection>

                <GuideSection icon={Sparkles} title="Routine hebdomadaire recommandée">
                    <p className="mb-4">
                        Pour maximiser vos économies, suivez cette routine simple chaque semaine :
                    </p>
                    
                    <div className="space-y-4">
                        <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-xl border-2 border-blue-200">
                            <div className="w-12 h-12 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center flex-shrink-0">
                                Jeu
                            </div>
                            <div>
                                <h4 className="font-bold text-blue-900">Jeudi - Découvrez le nouveau pack</h4>
                                <p className="text-blue-800 text-sm">Les nouveaux rabais sont publiés. Consultez le Pack Hebdomadaire pour voir les recettes et économies de la semaine.</p>
                            </div>
                        </div>
                        
                        <div className="flex items-start gap-4 p-4 bg-green-50 rounded-xl border-2 border-green-200">
                            <div className="w-12 h-12 rounded-full bg-green-500 text-white font-bold flex items-center justify-center flex-shrink-0">
                                Ven
                            </div>
                            <div>
                                <h4 className="font-bold text-green-900">Vendredi - Planifiez votre semaine</h4>
                                <p className="text-green-800 text-sm">Regardez les 5 soupers proposés et décidez lesquels vous conviennent. Ajustez les portions si nécessaire.</p>
                            </div>
                        </div>
                        
                        <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-xl border-2 border-amber-200">
                            <div className="w-12 h-12 rounded-full bg-amber-500 text-white font-bold flex items-center justify-center flex-shrink-0">
                                Sam
                            </div>
                            <div>
                                <h4 className="font-bold text-amber-900">Samedi/Dimanche - Faites vos courses</h4>
                                <p className="text-amber-800 text-sm">Utilisez la liste d'épicerie générée. Repérez les articles en rabais et regroupez vos achats par magasin.</p>
                            </div>
                        </div>
                        
                        <div className="flex items-start gap-4 p-4 bg-purple-50 rounded-xl border-2 border-purple-200">
                            <div className="w-12 h-12 rounded-full bg-purple-500 text-white font-bold flex items-center justify-center flex-shrink-0">
                                Lun
                            </div>
                            <div>
                                <h4 className="font-bold text-purple-900">Lundi à Vendredi - Cuisinez!</h4>
                                <p className="text-purple-800 text-sm">Suivez les recettes du pack. Chaque soir, vous avez un souper planifié avec tous les ingrédients nécessaires.</p>
                            </div>
                        </div>
                    </div>
                    
                    <TipBox variant="success">
                        <strong>Résultat :</strong> En suivant cette routine, vous pouvez économiser entre 30$ et 50$ par semaine sur votre épicerie tout en mangeant varié et équilibré!
                    </TipBox>
                </GuideSection>

                {/* CTA */}
                <div className="bg-gradient-cameleon rounded-3xl p-8 text-center text-white mt-8">
                    <h2 className="font-heading text-3xl font-black mb-4">Prêt à économiser?</h2>
                    <p className="text-white/80 mb-6 max-w-xl mx-auto">
                        Commencez dès maintenant avec le Pack Hebdomadaire et découvrez combien vous pouvez économiser cette semaine.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to="/weekly-pack">
                            <Button className="bg-white text-primary hover:bg-white/90 font-bold px-8">
                                <Calendar className="w-5 h-5 mr-2" />
                                Voir le Pack de la Semaine
                            </Button>
                        </Link>
                        <Link to="/register">
                            <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 font-bold px-8">
                                Créer un compte gratuit
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GuidePage;
